import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, where, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface SupportTicket {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    subject: string;
    content: string;
    type: 'General' | 'Account' | 'Finance' | 'Technical';
    status: 'pending' | 'in-progress' | 'answered' | 'closed';
    createdAt: Timestamp;
    updatedAt: Timestamp;
    adminReply?: string;
    adminRepliedAt?: Timestamp;
    adminName?: string;
}

export const useSupportTickets = (userId?: string) => {
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let q;

        if (userId) {
            // User view: only their tickets
            q = query(
                collection(db, 'support_tickets'),
                where('userId', '==', userId)
                // orderBy('createdAt', 'desc')
            );
        } else {
            // Admin view: all tickets
            q = query(
                collection(db, 'support_tickets'),
                orderBy('createdAt', 'desc')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as SupportTicket[];
            setTickets(data);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching support tickets:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const createTicket = async (data: Omit<SupportTicket, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => {
        try {
            await addDoc(collection(db, 'support_tickets'), {
                ...data,
                status: 'pending',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error creating ticket:', error);
            return { success: false, error };
        }
    };

    const replyToTicket = async (ticketId: string, reply: string, adminName: string) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                adminReply: reply,
                adminRepliedAt: Timestamp.now(),
                adminName,
                status: 'answered',
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error replying to ticket:', error);
            return { success: false, error };
        }
    };

    const updateTicketStatus = async (ticketId: string, status: SupportTicket['status']) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                status,
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating ticket status:', error);
            return { success: false, error };
        }
    };

    const updateReply = async (ticketId: string, newReply: string, adminName: string) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                adminReply: newReply,
                adminRepliedAt: Timestamp.now(),
                adminName,
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating reply:', error);
            return { success: false, error };
        }
    };

    const deleteReply = async (ticketId: string) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                adminReply: null,
                adminRepliedAt: null,
                adminName: null,
                status: 'pending',
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error deleting reply:', error);
            return { success: false, error };
        }
    };

    const deleteTicket = async (ticketId: string) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                status: 'closed',
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error deleting ticket:', error);
            return { success: false, error };
        }
    };

    return {
        tickets,
        loading,
        createTicket,
        replyToTicket,
        updateTicketStatus,
        updateReply,
        deleteReply,
        deleteTicket
    };
};
